import type { DateTime } from 'luxon';
import axios from 'axios';
import merge from 'lodash/merge';

/**
 * MaterialsService
 */
export default class CoursesService
{
    public static path = 'admin/elearning/courses/courses';
    public static reportPath = 'elearning/reports';

    /**
     * @param filter FilterModel
     * @param pager Pager
     *
     * @returns Promise<Pagination<Resource<ListItemModel>>>
     */
    public static async getList(filter: FilterModel, pager: Pager): Promise<Pagination<Resource<ListItemModel>>>
    {
        return (await axios.get<Pagination<Resource<ListItemModel>>>(this.path, {
            params: merge({}, filter, pager.data())
        })).data;
    }

    /**
     * @param id number
     *
     * @returns Promise<Resource<FormModel>>
     */
    public static async fetch(id: number): Promise<Resource<FormModel>>
    {
        return (await axios.get<Resource<FormModel>>(`${this.path}/${id}`)).data;
    }

    /**
     * @param coursePublicIds string[]
     *
     * @returns Promise<CourseInfoModel[]>
     */
    public static async getCourseInfo(coursePublicIds: string[]): Promise<CourseInfoModel[]>
    {
        return (await axios.post<CourseInfoModel[]>(`${this.path}/info`, { coursePublicIds })).data;
    }

    /**
     * @param search string
     * @param limit number
     *
     * @returns Promise<ListItemModel[]>
     */
    public static async getAutocompleteList(search: string, websiteId: number, locale: string, limit: number = 10, includeCyclicalCourses: boolean = true): Promise<ListItemModel[]>
    {
        return (await axios.get<ListItemModel[]>(`${this.path}/autocomplete`, {
            params: {
                search: search,
                websiteId,
                locale,
                limit: limit,
                includeCyclicalCourses: includeCyclicalCourses
            }
        })).data;
    }

    /**
     * @param id number
     *
     * @returns Promise<string>
     */
    public static async preview(id: number): Promise<string>
    {
        return (await axios.get<string>(`${this.path}/${id}/preview`)).data;
    }

    /**
     * @param model FormModel
     *
     * @returns Promise<Resource<FormModel>>
     */
    public static async create(model: FormModel): Promise<Resource<FormModel>>
    {
        return (await axios.post<Resource<FormModel>>(this.path, model)).data;
    }

    /**
     * @param id number
     * @param model FormModel
     *
     * @returns Promise<Statement>
     */
    public static async update(id: number, model: FormModel): Promise<Statement>
    {
        return (await axios.put<Statement>(`${this.path}/${id}`, model)).data;
    }

    /**
     * @param id number
     *
     * @returns Promise<Statement>
     */
    public static async clone(id: number): Promise<Resource<FormModel>>
    {
        return (await axios.post<Resource<FormModel>>(`${this.path}/${id}/clone`)).data;
    }

    /**
     * @param id number
     *
     * @returns Promise<Statement>
     */
    public static async archive(id: number): Promise<Statement>
    {
        return (await axios.put(`${this.path}/${id}/archive`)).data as Statement;
    }

    /**
     * @param id number
     * @param expanded number[]
     *
     * @returns Promise<Pagination<Resource<ProgramListModel>>>
     */
    public static async getProgram(id: number, expanded: number[]): Promise<Pagination<Resource<ProgramListModel>>>
    {
        return (await axios.get<Pagination<Resource<ProgramListModel>>>(`admin/elearning/courses/units/${id}/program`, {
            params: {
                expanded: expanded.join(',')
            }
        })).data;
    }

    /**
     * @param id number
     *
     * @returns Promise<Blob>
     */
    public static async getResultsReport(id: number): Promise<Blob>
    {
        const response = (await axios.get<Blob>(`${this.reportPath}/courses/${id}/all-results`, { responseType: 'blob' }));

        return response.data;
    }

    /**
     * @param id number
     *
     * @returns string
     */
    public static getResultsReportUrl(id: number): string
    {
        const uri = axios.getUri({ url: `${this.reportPath}/courses/${id}/all-results` });

        return axios.defaults.baseURL + '/' + uri;
    }

    /**
     * @param courseId number
     * @param pager Pager
     *
     * @returns Promise<Pagination<ListItemModel>>
     */
    public static async getParticipants(courseId: number, pager: Pager, filter: FilterModel): Promise<Pagination<ParticipantItemModel>>
    {
        return (await axios.get<Pagination<ParticipantItemModel>>(`${this.path}/${courseId}/participants`, {
            params: merge({}, filter, pager.data())
        })).data;
    }

    /**
     * @param courseId number
     * @param model FormModel
     *
     * @returns Promise<ParticipantModel>
     */
    public static async addParticipant(courseId: number, model: ParticipantModel): Promise<ParticipantModel>
    {
        return (await axios.post<ParticipantModel>(`${this.path}/${courseId}/participants/${model.userId}`, model)).data;
    }

    /**
     * @param courseId number
     * @param model FormModel
     *
     * @returns Promise<Statement>
     */
    public static async updateParticipant(courseId: number, model: ParticipantModel): Promise<Statement>
    {
        return (await axios.put<Statement>(`${this.path}/${courseId}/participants/${model.userId}`, model)).data;
    }

    /**
     * @param courseId number
     * @param id number
     *
     * @returns Promise<Statement>
     */
    public static async removeParticipant(courseId: number, id: number): Promise<Statement>
    {
        return (await axios.delete(`${this.path}/${courseId}/participants/${id}`)).data as Statement;
    }

    /**
     * @param courseId number
     * @param pager Pager
     *
     * @returns Promise<Pagination<ListItemModel>>
     */
    public static async getCourseTeams(courseId: number, pager: Pager): Promise<Pagination<CourseTeamItemModel>>
    {
        return (await axios.get<Pagination<CourseTeamItemModel>>(`${this.path}/${courseId}/courseteams`, {
            params: pager.data()
        })).data;
    }

    /**
     * @param courseId number
     * @param pager Pager
     * @param userId number
     *
     * @returns Promise<Pagination<ListItemModel>>
     */
    public static async getCourseTeamsByUserId(courseId: number, pager: Pager, userId: number): Promise<Pagination<CourseTeamItemModel>>
    {
        return (await axios.get<Pagination<CourseTeamItemModel>>(`${this.path}/${courseId}/courseteams/${userId}`, {
            params: pager.data()
        })).data;
    }

    /**
     * @param courseId number
     * @param model FormModel
     *
     * @returns Promise<CourseTeamModel>
     */
    public static async addCourseTeam(courseId: number, model: CourseTeamModel): Promise<CourseTeamModel>
    {
        return (await axios.post<CourseTeamModel>(`${this.path}/${courseId}/courseteams/${model.teamId}`, model)).data;
    }

    /**
     * @param courseId number
     * @param model FormModel
     *
     * @returns Promise<Statement>
     */
    public static async updateCourseTeam(courseId: number, model: CourseTeamModel): Promise<Statement>
    {
        return (await axios.put<Statement>(`${this.path}/${courseId}/courseteams/${model.teamId}`, model)).data;
    }

    /**
     * @param courseId number
     * @param id number
     *
     * @returns Promise<Statement>
     */
    public static async removeCourseTeam(courseId: number, id: number): Promise<Statement>
    {
        return (await axios.delete(`${this.path}/${courseId}/courseteams/${id}`)).data as Statement;
    }

    /**
     * @param id number
     *
     * @returns Promise<Resource<FormModel>>
     */
    public static async fetchAutocompleteItem(id: number, websiteId: number, locale: string): Promise<ListItemModel>
    {
        return (await axios.get<ListItemModel>(`${this.path}/autocomplete/${id}`, {
            params: {
                websiteId,
                locale
            }
        })).data;
    }

    /**
     * @param id number
     * @param expanded number[]
     *
     * @returns Promise<RedirectUnitsListModel>
     */
    public static async getUnitsToRedirect(id: number): Promise<RedirectUnitsListModel[]>
    {
        return (await axios.get<RedirectUnitsListModel[]>(`admin/elearning/courses/units/${id}/unit-redirects`)).data;
    }
}

export interface FilterModel
{
    name: string;
    isPublished?: boolean;
    categoryId: number;
    isArchived: boolean;
    pageId?: number;
    isCyclical: boolean;
}

export interface RedirectUnitsListModel
{
    id: number;
    name: string;
    position: number;
    originId: number;
    module: string;
}

export interface ProgramListModel
{
    id: number;
    parentId?: number;
    folderId: number;
    name: string;
    option: string;
    module: string;
    pageMode: string;
    level: number;
    isActive: boolean;
    hasChildren: boolean;
    position: number;
    originId: number;
    isRequired: boolean;
}

export interface ListItemModel
{
    id: number;
    name: string;
    isPublished: boolean;
    createdBy: string;
    isArchived: boolean;
    aiStatus: boolean | null;
    dateCreatedUtc: DateTime;
    dateModifiedUtc: DateTime;
}

export interface CourseListAutocompleteModel
{
    id: number;
    name: string;
}

export interface FormModel extends PageModel
{
    description: string;
    categoryId?: number;
    isPublished: boolean;
    teaser: string;
    picture?: FileResource;
    certificateId?: number;
    requiredOrderOfCompletion: boolean;
    isChatOn: boolean;
    pendingCourseNotification: boolean;
    pendingCourseDaysInterval: number;
    pendingCourseDaysStarted: number;
    courseEndingNotification: boolean;
    courseEndingNotificationSendingDays: any;
    unreadenMessagesNotification: boolean;
    unreadenMessagesAdminNotification: boolean;
    isSurveyOn: boolean;
    beforeCourseSurveyCategoryId: number;
    beforeCourseSurveyId: number;
    afterCourseSurveyId: number;
    afterCourseSurveyCategoryId: number;
    isUsedInLearningPath: boolean;
    websiteId?: number;
    locale: string;
    datePublication: DateTime;
    isCourseCyclical: boolean;
    daysToCompleteCourse: number;
    daysToNextAttempt: number;
    daysToCertificateExpiration: number;
    cycleLimit: number;
    aiStatus: boolean | null;
}

export interface PageModel
{
    id: number;
    name: string;
    pageParentId?: number;
    symbol: string;
    layout: string;
    isActive: boolean;
    isHidden: boolean;
}

export interface ParticipantItemModel
{
    id: number;
    userId: number;
    dateFromUtc: DateTime;
    dateToUtc: DateTime;
    givenName: string;
    surname: string;
    email: string;
    facePictureUrl: string;
    manuallyAdded: boolean;
    meetingAccess: boolean;
}

export interface ParticipantModel
{
    id: number;
    userId: number;
    dateFromUtc?: DateTime;
    dateToUtc?: DateTime;
}

export interface CourseTeamItemModel
{
    id: number;
    teamId: number;
    dateFromUtc: DateTime;
    dateToUtc: DateTime;
    name: string;
    teamMembersCount: number;
    coordinatorsCount: number;
}

export interface CourseTeamModel
{
    id: number;
    teamId: number;
    dateFromUtc?: DateTime;
    dateToUtc?: DateTime;
}

export interface DashboardModel
{
    id: number;
    name: string;
    description: string;
    isPublished: boolean;
    certificateId?: number;
}

export interface CourseInfoModel
{
    publicId: string;
    name: string;
}
